import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FiSearch, 
  FiPackage,
  FiTruck
} from 'react-icons/fi';
import api from '../utils/api';
import './TrackShipment.css';

const TrackShipment = ({ showHeader = true, compact = false }) => {
  const navigate = useNavigate();
  const [trackingType, setTrackingType] = useState('airwaybill');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleTrack = async (e) => {
    e.preventDefault();
    if (!trackingNumber.trim()) {
      setError('Please enter a tracking number');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Changed to POST request
      const response = await api.post(`/track/${trackingType}/${trackingNumber}`);
      // Extract data from response structure
      const data = response.data?.data || response.data;
      
      // Navigate to results page with tracking data
      navigate('/tracking-results', { state: { shipmentData: data } });
    } catch (err) {
      setError(err.response?.data?.message || 'Shipment not found. Please check your tracking number.');
      setIsLoading(false);
    }
  };

  return (
    <section className={`track-shipment ${compact ? 'track-compact' : ''}`}>
      <div className="container">
        {showHeader && !compact && (
          <motion.div
            className="track-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="track-title">Track Your Shipment</h2>
            <p className="track-subtitle">Enter your tracking number to get real-time updates</p>
          </motion.div>
        )}

        <motion.div
          className="track-container"
          initial={{ opacity: 0, y: compact ? 10 : 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: compact ? 0.3 : 0.6, delay: compact ? 0 : 0.2 }}
        >
          <div className="track-card">
            {showHeader && !compact && (
            <div className="track-card-header">
              <div className="track-icon-wrapper">
                <FiTruck className="track-icon" />
              </div>
              <div className="track-header-content">
                <h3 className="track-card-title">Track Your Package</h3>
                <p className="track-card-subtitle">Real-time tracking information at your fingertips</p>
              </div>
            </div>
          )}

            <form className="track-form" onSubmit={handleTrack}>
              <div className="track-type-label">
                <label>Select Tracking Type</label>
              </div>
              <div className="track-type-selector">
                <button
                  type="button"
                  className={`track-type-btn ${trackingType === 'airwaybill' ? 'active' : ''}`}
                  onClick={() => setTrackingType('airwaybill')}
                >
                  <FiPackage />
                  <span>Airwaybill Number</span>
                </button>
                <button
                  type="button"
                  className={`track-type-btn ${trackingType === 'shipper_reference' ? 'active' : ''}`}
                  onClick={() => setTrackingType('shipper_reference')}
                >
                  <FiPackage />
                  <span>Reference Number</span>
                </button>
              </div>

              <div className="track-input-wrapper">
                <div className="track-input-label">
                  <label>
                    {trackingType === 'airwaybill' ? 'Airwaybill Number' : 'Reference Number'}
                  </label>
                </div>
                <div className="track-input-group">
                  <div className="track-input-container">
                    <FiSearch className="track-input-icon" />
                    <input
                      type="text"
                      className="track-input"
                      placeholder={
                        trackingType === 'airwaybill'
                          ? 'Enter your airwaybill number'
                          : 'Enter your reference number'
                      }
                      value={trackingNumber}
                      onChange={(e) => {
                        setTrackingNumber(e.target.value);
                        // Clear error when user starts typing
                        if (error) {
                          setError(null);
                        }
                      }}
                    />
                  </div>
                  <motion.button
                    type="submit"
                    className="track-button"
                    disabled={isLoading}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {isLoading ? (
                      <>
                        <span className="track-spinner"></span>
                        Tracking...
                      </>
                    ) : (
                      <>
                        <FiSearch />
                        Track
                      </>
                    )}
                  </motion.button>
                </div>
              </div>

              {error && (
                <motion.div
                  className="track-error"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  <span className="track-error-icon">!</span>
                  <span className="track-error-message">{error}</span>
                  <button
                    type="button"
                    className="track-error-close"
                    onClick={() => setError(null)}
                    aria-label="Close error"
                  >
                    ×
                  </button>
                </motion.div>
              )}
            </form>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TrackShipment;
